// src/hooks/usePolling.js
//
// Calls `fetcher()` immediately, then every `intervalMs` while the page is
// visible AND `shouldPoll(latestResult)` returns true. Stops polling otherwise.
//
// Returns { data, error, loading, refresh }.

import { useCallback, useEffect, useRef, useState } from 'react';

export function usePolling(fetcher, { intervalMs = 5000, shouldPoll, enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const aliveRef = useRef(true);

  const tick = useCallback(async () => {
    if (!aliveRef.current) return;
    try {
      const result = await fetcher();
      if (!aliveRef.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!aliveRef.current) return;
      setError(err);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [fetcher]);

  const scheduleNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!enabled) return;
    if (document.visibilityState !== 'visible') return;
    if (typeof shouldPoll === 'function' && !shouldPoll(data)) return;
    timerRef.current = setTimeout(async () => {
      await tick();
      scheduleNext();
    }, intervalMs);
  }, [enabled, shouldPoll, data, tick, intervalMs]);

  // Initial fetch + reschedule whenever shouldPoll/data change.
  useEffect(() => {
    aliveRef.current = true;
    if (enabled) tick();
    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // tick changes when fetcher changes; we WANT a re-fetch in that case.
  }, [tick, enabled]);

  // Reschedule when data or shouldPoll changes.
  useEffect(() => {
    scheduleNext();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [scheduleNext]);

  // Pause/resume on visibility change.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') scheduleNext();
      else if (timerRef.current) clearTimeout(timerRef.current);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [scheduleNext]);

  const refresh = useCallback(() => {
    setLoading(true);
    tick();
  }, [tick]);

  return { data, error, loading, refresh };
}
